/**
 * Supabase Storage helpers — replaces Firebase Storage
 */

import { supabase } from '../supabase';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL!;

// ── Buckets ───────────────────────────────────────────────────────────────────
const BUCKET_PROFILE_PHOTOS = 'profile-photos';
const BUCKET_PROJECT_FILES = 'project-files';

// ── Upload ────────────────────────────────────────────────────────────────────

/**
 * Upload a profile photo.
 * Path inside bucket: {userId}/{filename}
 * Returns the public URL.
 */
export async function uploadProfilePhoto(userId: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'jpg';
  const path = `${userId}/profile.${ext}`;

  const { error } = await supabase.storage
    .from(BUCKET_PROFILE_PHOTOS)
    .upload(path, file, { upsert: true, contentType: file.type });

  if (error) throw error;
  return getPublicUrl(BUCKET_PROFILE_PHOTOS, path);
}

/**
 * Upload a project image.
 * Returns the public URL.
 */
export async function uploadProjectImage(userId: string, file: File, projectId: string): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'jpg';
  const path = `${userId}/${projectId}/${Date.now()}.${ext}`;

  const { error } = await supabase.storage
    .from(BUCKET_PROJECT_FILES)
    .upload(path, file, { upsert: false, contentType: file.type });

  if (error) throw error;
  return getPublicUrl(BUCKET_PROJECT_FILES, path);
}

// ── Delete ────────────────────────────────────────────────────────────────────

export async function deleteProfilePhoto(userId: string): Promise<void> {
  // Try common extensions
  for (const ext of ['jpg', 'jpeg', 'png', 'webp', 'gif']) {
    const { error } = await supabase.storage
      .from(BUCKET_PROFILE_PHOTOS)
      .remove([`${userId}/profile.${ext}`]);
    if (!error) break;
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getPublicUrl(bucket: string, path: string): string {
  return `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${path}`;
}
